import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ENV } from '../config/env.js';
import { parseApiEnvelopeResponse } from '../firebase/config.js';
import LearnMode from '../components/LearnMode.jsx';
import ContentRenderer from '../components/ContentRenderer.jsx';

const API_BASE_URL = ENV.apiBaseUrl;

export default function PublicStudyPage() {
    const { shareId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [index, setIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [mode, setMode] = useState('cards'); // 'cards' | 'learn'

    useEffect(() => {
        (async () => {
            const url = `${API_BASE_URL}/api/shared/${shareId}`;
            try {
                const res = await fetch(url);
                const data = await parseApiEnvelopeResponse(res, { url });
                setData(data);
            } catch (err) {
                setError(err.code === 'NOT_FOUND' ? 'This set could not be found or is no longer shared.' : (err.message || 'Failed to load study set.'));
            } finally {
                setLoading(false);
            }
        })();
    }, [shareId]);

    if (loading) return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
            <div className="h-8 w-8 rounded-full border-2 border-teal-400/30 border-t-teal-400 animate-spin" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-center">
            <div className="text-5xl mb-4">😕</div>
            <h1 className="text-xl font-semibold text-white mb-2">Set not found</h1>
            <p className="text-slate-400 text-sm mb-6 max-w-sm">{error}</p>
            <button onClick={() => navigate('/')} className="rounded-full px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600">
                Go to FlashFonic
            </button>
        </div>
    );

    const cards = data?.cards || [];
    const card = cards[index];

    if (mode === 'learn') {
        const folder = { id: shareId, name: data.name, cards };
        return <LearnMode folder={folder} onClose={() => setMode('cards')} />;
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white">
            {/* Top bar */}
            <header className="border-b border-white/8 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
                    <div className="min-w-0">
                        <p className="text-xs text-teal-400 font-medium">Shared Study Set</p>
                        <h1 className="text-sm font-semibold text-white truncate">{data.name}</h1>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-slate-400 bg-white/5 rounded-full px-2.5 py-1">{cards.length} cards</span>
                        <button onClick={() => navigate('/')}
                            className="rounded-full px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 transition">
                            Get FlashFonic
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
                {/* Mode switcher + counter */}
                <div className="flex items-center justify-between">
                    <div className="flex gap-1 bg-white/5 rounded-xl p-1">
                        <button onClick={() => setMode('cards')}
                            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition ${mode === 'cards' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
                            Flashcards
                        </button>
                        <button onClick={() => setMode('learn')}
                            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition ${mode === 'learn' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
                            Learn
                        </button>
                    </div>
                    <p className="text-sm text-slate-400 tabular-nums">{index + 1} / {cards.length}</p>
                </div>

                {/* Card */}
                {card && (
                    <div className="w-full" style={{ perspective: 1200 }}>
                        <div
                            onClick={() => setIsFlipped(f => !f)}
                            className="relative w-full cursor-pointer select-none"
                            style={{
                                minHeight: 220,
                                transformStyle: 'preserve-3d',
                                transform: isFlipped ? 'rotateY(180deg)' : 'none',
                                transition: 'transform 0.4s ease',
                            }}
                        >
                            {/* Front */}
                            <div className="absolute inset-0 rounded-2xl border border-white/10 bg-slate-900/80 flex flex-col items-center justify-center p-8 text-center backface-hidden">
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-400/70 mb-3">Front</p>
                                <p className="text-white text-lg leading-relaxed"><ContentRenderer content={card.question} /></p>
                                <p className="absolute bottom-4 text-[10px] text-slate-600">Click to flip</p>
                            </div>
                            {/* Back */}
                            <div className="absolute inset-0 rounded-2xl border border-teal-500/20 bg-teal-500/5 flex flex-col items-center justify-center p-8 text-center backface-hidden"
                                style={{ transform: 'rotateY(180deg)' }}>
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-400/70 mb-3">Back</p>
                                <p className="text-white text-lg leading-relaxed"><ContentRenderer content={card.answer} /></p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Nav */}
                <div className="flex items-center justify-center gap-4">
                    <button
                        onClick={() => { setIndex(i => Math.max(0, i - 1)); setIsFlipped(false); }}
                        disabled={index === 0}
                        className="rounded-full p-3 bg-white/5 hover:bg-white/10 disabled:opacity-30 transition"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    {/* Dots */}
                    <div className="flex gap-1.5 max-w-xs overflow-hidden">
                        {cards.slice(Math.max(0, index - 4), index + 5).map((_, dotI) => {
                            const actualI = Math.max(0, index - 4) + dotI;
                            return (
                                <button key={actualI} onClick={() => { setIndex(actualI); setIsFlipped(false); }}
                                    className={`h-1.5 rounded-full transition-all ${actualI === index ? 'w-5 bg-teal-400' : 'w-1.5 bg-white/20 hover:bg-white/40'}`} />
                            );
                        })}
                    </div>
                    <button
                        onClick={() => { setIndex(i => Math.min(cards.length - 1, i + 1)); setIsFlipped(false); }}
                        disabled={index === cards.length - 1}
                        className="rounded-full p-3 bg-white/5 hover:bg-white/10 disabled:opacity-30 transition"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                </div>

                {/* Card list */}
                <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">All terms</p>
                    {cards.map((c, i) => (
                        <button key={i} onClick={() => { setIndex(i); setIsFlipped(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            className={`w-full text-left rounded-xl border px-4 py-3 transition ${i === index ? 'border-teal-500/30 bg-teal-500/8' : 'border-white/8 bg-white/3 hover:bg-white/6'}`}>
                            <div className="flex items-start gap-4">
                                <span className="text-xs text-slate-500 tabular-nums mt-0.5 shrink-0 w-5">{i + 1}</span>
                                <div className="min-w-0 grid grid-cols-2 gap-4 flex-1">
                                    <p className="text-sm text-white truncate"><ContentRenderer content={c.question} /></p>
                                    <p className="text-sm text-slate-400 truncate"><ContentRenderer content={c.answer} /></p>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Footer CTA */}
                <div className="text-center py-6 border-t border-white/8">
                    <p className="text-sm text-slate-400 mb-3">Want to create your own flashcard sets?</p>
                    <button onClick={() => navigate('/')}
                        className="rounded-full px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 transition">
                        Try FlashFonic Free
                    </button>
                </div>
            </main>
        </div>
    );
}
